import React from "react";
import styled from "styled-components";
import {
  ImageCopyItem,
  ImageCopyItemClassName,
  ImageCopyItemProps,
  LinkNext,
  PageSection,
  PageSectionClassName,
  PageSectionProps,
  Theme,
} from "@literati/public-ui-shared-react";
import analytics from "analytics";

/** This is the className that will be pre-pended to all of TeacherPartnerSectionRewards's inner component classNames */
export const TEACHER_PARTNER_SECTION_REWARDS =
  "teacher-partner-section-rewards";

/** These are all of the internal classNames that are used within the TeacherPartnerSectionRewards component */
export const TeacherPartnerSectionRewardsClassName = {
  BASE: TEACHER_PARTNER_SECTION_REWARDS,
  CONTENT: `${TEACHER_PARTNER_SECTION_REWARDS}-content`,
  ITEMS_GRID: `${TEACHER_PARTNER_SECTION_REWARDS}-items-grid`,
  LINK: `${TEACHER_PARTNER_SECTION_REWARDS}-link`,
} as const;

export interface TeacherPartnerSectionRewardsProps
  extends Omit<PageSectionProps, "children"> {
  className?: string;
  ctaLabel?: string;
  ctaLink: string;
  items?: ImageCopyItemProps[];
}

const StyledPageSection = styled(PageSection)`
  .${PageSectionClassName.CONTENT_SPACING_HEADER} {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .${PageSectionClassName.SUBTEXT} {
    max-width: 930px;
  }
`;

const RewardsContent = styled.div`
  margin: 3rem 0 0;

  ${({ theme }) => theme.media.sm} {
    margin: 3rem 1rem 0;
  }

  ${({ theme }) => theme.media.lg} {
    margin: 3rem 3rem 0;
  }
`;

const RewardsItemsGrid = styled.div`
  display: grid;
  grid-template-rows: repeat(3, 1fr);
  grid-template-columns: 1fr;
  gap: 2.625rem 0;
  justify-items: center;
  margin: 3rem 0;

  ${({ theme }) => theme.media.lg} {
    gap: 0 2rem;
  }

  ${({ theme }) => theme.media.lg} {
    grid-template-rows: 1fr;
    grid-template-columns: repeat(3, 1fr);
    gap: 0 3rem;
  }
`;

const RewardsImageCopyItem = styled(ImageCopyItem)`
  .${ImageCopyItemClassName.PRIMARY_COPY} {
    margin: 1rem 0 0;
  }

  .${ImageCopyItemClassName.SUBTEXT} {
    max-width: 316px;
  }
`;

const StyledLinkNext = styled(LinkNext)`
  margin: auto;
`;

export const ITEMS_DEFAULT = [
  {
    imageProps: {
      alt: "Illustration of a book spread open with stars on the pages.",
      height: 100,
      src: "https://images.ctfassets.net/vqtz4cq2w5ts/4MHAKhU1uT2WOyB1ModuVu/6477e72dbd4cad75451b8429731ff48d/Become_Member_Icon.svg",
      width: 100,
    },
    primaryCopy: "Become a Member",
    subtext:
      "Get your child started on their personalized book club experience—reluctant readers to already-avid bookworms welcome!",
  },
  {
    imageProps: {
      alt: "Illustration of someone visiting the Literati website.",
      height: 100,
      src: "https://images.ctfassets.net/vqtz4cq2w5ts/4md7ip6bWLKpD9ufYi9APx/564b8f7cb49b0b1932505d6df4af71d6/Support_School_Icon.svg",
      width: 100,
    },
    primaryCopy: "Support Their School",
    subtext:
      "For every membership you buy, Literati credits $100 in bonus rewards back to your child’s school.",
  },
  {
    imageProps: {
      alt: "Illustration of a stack of books with stars above them.",
      height: 100,
      src: "https://images.ctfassets.net/vqtz4cq2w5ts/7xPHbastMtfSfMOO50XOIJ/6b0698f4f27f715af7e9de11e3facdf6/Year_Round_Reading_Icon.svg",
      width: 100,
    },
    primaryCopy: "Year-Round Reading",
    subtext:
      "Build reading skills at home and over school breaks with an engaging experience that includes personalized bookplate stickers!",
  },
];

export const TeacherPartnerSectionRewards = ({
  className = "",
  ctaLabel = "Get Started",
  ctaLink,
  items = ITEMS_DEFAULT,
  ...pageSectionProps
}: TeacherPartnerSectionRewardsProps) => {
  const cls = TeacherPartnerSectionRewardsClassName;

  const handleLinkClick = () =>
    analytics.trackAmplitudeClickEvent(
      "Get Started",
      "TeacherPartnerSectionRewards"
    );

  return (
    <Theme>
      <StyledPageSection
        className={`${TeacherPartnerSectionRewardsClassName.BASE} ${className}`}
        {...pageSectionProps}
      >
        <RewardsContent className={cls.CONTENT}>
          <RewardsItemsGrid className={cls.ITEMS_GRID}>
            {items?.map((content, idx) => (
              <RewardsImageCopyItem
                key={`rewards-item-${idx}`}
                variant="small"
                {...content}
              />
            ))}
          </RewardsItemsGrid>
          <StyledLinkNext
            className={cls.LINK}
            href={ctaLink}
            legacyBehavior={false}
            onClick={handleLinkClick}
            variant="button"
          >
            {" "}
            {ctaLabel}
          </StyledLinkNext>
        </RewardsContent>
      </StyledPageSection>
    </Theme>
  );
};

export default TeacherPartnerSectionRewards;
